import { useEffect, useState, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * PhotoLightbox — full-screen photo viewer with swipe/click carousel.
 *
 * Open by passing `photos` (array of URLs) and `startIndex`. Pass
 * `onClose` to dismiss. Mounts at document body level via fixed
 * positioning so it overlays modals.
 */
export default function PhotoLightbox({ photos, startIndex = 0, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const startX = useRef(null);

  const next = useCallback(() => setIndex(i => (i + 1) % photos.length), [photos.length]);
  const prev = useCallback(() => setIndex(i => (i - 1 + photos.length) % photos.length), [photos.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [next, prev, onClose]);

  if (!photos?.length) return null;

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx > 50) prev();
    else if (dx < -50) next();
    startX.current = null;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center select-none"
      role="dialog" aria-modal="true" data-testid="photo-lightbox"
      onClick={onClose}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Close */}
      <button onClick={(e) => { e.stopPropagation(); onClose?.(); }} data-testid="lightbox-close"
        aria-label="Close"
        className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
        <X size={20}/>
      </button>

      {/* Counter pill */}
      {photos.length > 1 && (
        <span className="absolute top-6 left-1/2 -translate-x-1/2 text-[12px] tracking-wider font-semibold uppercase text-white/90 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full">
          {index + 1} / {photos.length}
        </span>
      )}

      {/* Main image */}
      <img src={photos[index]} alt={`Photo ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] object-contain shadow-2xl rounded-md"
        data-testid="lightbox-image" />

      {/* Prev / next arrows (hidden on single photo) */}
      {photos.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev"
            aria-label="Previous photo"
            className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md items-center justify-center text-white">
            <ChevronLeft size={26}/>
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next"
            aria-label="Next photo"
            className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md items-center justify-center text-white">
            <ChevronRight size={26}/>
          </button>

          {/* Dot indicators */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5">
            {photos.map((_, i) => (
              <button key={i} onClick={(e) => { e.stopPropagation(); setIndex(i); }}
                aria-label={`Go to photo ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-all ${i === index ? 'bg-white w-6' : 'bg-white/40'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
